import type { PlatformModuleCatalogEntry } from '../services/api/types/modules';
import { STUDENT_PORTAL_PATH } from './assignments';

/**
 * Navigation metadata for Super Admin / MODULE_CODES entries that should appear
 * in the tenant sidebar. Used when `platform_modules` is missing `route_path`
 * (catalog API omits those rows) so enabled subscriptions still get a screen.
 *
 * Keep in sync with rust_school `MODULE_NAV_SPECS` for these codes.
 */
export const PLATFORM_MODULE_NAV_DEFAULTS: PlatformModuleCatalogEntry[] = [
  {
    module_code: 'portal',
    display_name: 'My School',
    sort_order: 50,
    route_path: STUDENT_PORTAL_PATH,
    icon: 'GraduationCap',
    component: 'StudentPortal',
    screen_id: 'student-portal',
  },
  {
    module_code: 'announcements',
    display_name: 'Announcements',
    sort_order: 360,
    route_path: '/announcements',
    icon: 'Megaphone',
    component: 'Announcements',
    screen_id: 'announcements',
  },
  {
    module_code: 'enrollment',
    display_name: 'Session promotion',
    sort_order: 370,
    route_path: '/session-promotion',
    icon: 'ArrowRightLeft',
    component: 'SessionPromotion',
    screen_id: 'session-promotion',
  },
  {
    module_code: 'staff_leave',
    display_name: 'Leave',
    sort_order: 95,
    route_path: '/staff-leave',
    icon: 'CalendarClock',
    component: 'StaffLeave',
    screen_id: 'staff-leave',
  },
  {
    module_code: 'pickup_pass',
    display_name: 'Issue pickup PIN / QR',
    sort_order: 265,
    route_path: '/pickup-pass',
    icon: 'Key',
    component: 'IssuePickupPass',
    screen_id: 'pickup-pass',
  },
  {
    module_code: 'expense',
    display_name: 'Expenses',
    sort_order: 235,
    route_path: '/expenses',
    icon: 'Receipt',
    component: 'Expenses',
    screen_id: 'expenses',
  },
];

const NAV_DEFAULTS_BY_CODE = new Map(
  PLATFORM_MODULE_NAV_DEFAULTS.map((entry) => [entry.module_code, entry])
);

/** Fill missing catalog rows (or null route_path) from frontend nav defaults. */
export function mergeCatalogWithNavDefaults(
  catalog: PlatformModuleCatalogEntry[],
  enabledCodes: Set<string> | null
): PlatformModuleCatalogEntry[] {
  const byCode = new Map<string, PlatformModuleCatalogEntry>();

  for (const entry of catalog) {
    const code = entry.module_code?.trim();
    if (!code) continue;
    const path = entry.route_path?.trim();
    if (path) {
      byCode.set(code, entry);
      continue;
    }
    const fallback = NAV_DEFAULTS_BY_CODE.get(code);
    if (fallback) {
      byCode.set(code, {
        ...fallback,
        display_name: entry.display_name?.trim() || fallback.display_name,
        sort_order: entry.sort_order ?? fallback.sort_order,
      });
    }
  }

  const codesToEnsure =
    enabledCodes ?? new Set(PLATFORM_MODULE_NAV_DEFAULTS.map((entry) => entry.module_code));

  Array.from(codesToEnsure).forEach((code) => {
    if (byCode.has(code)) return;
    const fallback = NAV_DEFAULTS_BY_CODE.get(code);
    if (fallback) byCode.set(code, fallback);
  });

  return Array.from(byCode.values()).sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.module_code.localeCompare(b.module_code)
  );
}
